// src/hooks/dashboard/useLeafCategorySnapshots.ts

import { useMemo } from "react";
import { useCategoriesTree } from "@/hooks/dashboard/useCategoriesTree";
import { useCategorySnapshots } from "@/hooks/dashboard/useCategorySnapshots";

export type LeafCategorySnapshot = {
  categoryId: number;
  code: string;
  name: string;
  parentCode: string | null;
  parentName: string | null;
  isLeaf: boolean;
  snap: {
    current: Record<string, number | null>;
    ratios: Record<string, number | null>;
    changes: Record<string, Record<string, { time_window: string; date: string; value: number | null }>>;
    rollups: Record<string, Record<string, { time_window: string; date: string; value: number | null }>>;
  };
};

export function useLeafCategorySnapshots() {
  const { roots, maps } = useCategoriesTree();
  const { byCategory, isLoading, isError, error } = useCategorySnapshots();

  const leaves = useMemo<LeafCategorySnapshot[]>(() => {
    const allCodes = Object.keys(maps.codeToId || {});

    const leafCodes = (() => {
      const leaf = new Set<string>();
      const walk = (node: { category_code: string; children?: unknown[] }) => {
        const kids = (node.children as Array<{ category_code: string; children?: unknown[] }>) || [];
        if (!kids.length) leaf.add(node.category_code);
        kids.forEach((c) => walk(c));
      };
      roots.forEach((r) => walk(r as unknown as { category_code: string; children?: unknown[] }));
      return leaf;
    })();

    const out: LeafCategorySnapshot[] = [];
    for (const code of allCodes) {
      if (!leafCodes.has(code)) continue;
      const id = maps.codeToId[code];
      if (!id) continue;
      const name = maps.codeToName[code] || code;
      const parentCode = maps.parentByCode[code] || null;
      const parentName = parentCode ? (maps.codeToName[parentCode] || null) : null;
      const snap = byCategory[id];
      if (!snap) continue;
      out.push({ categoryId: id, code, name, parentCode, parentName, isLeaf: true, snap });
    }
    return out;
  }, [roots, maps, byCategory]);

  return { leaves, isLoading, isError, error } as const;
}


